import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { data: auth, isLoading: loading } = useAuth()
    const location = useLocation()

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FFF4EC]">
                <div className="flex flex-col items-center gap-4 group">
                    <div className="relative h-12 w-12">
                        <div className="absolute inset-0 rounded-xl border-4 border-pink-100"></div>
                        <div className="absolute inset-0 rounded-xl border-4 border-pink-600 border-t-transparent animate-spin"></div>
                    </div>
                </div>
            </div>
        )
    }

    const user = auth
    const profileComplete = auth?.isProfileComplete
    const subscriptionActive = auth?.subscriptionActive

    if (!user) {
        return <Navigate to="/login" replace />
    }

    const currentPath = location.pathname.replace(/\/$/, '')
    const isCompleteProfilePath = currentPath === '/complete-profile'
    const isSubscriptionPath = currentPath === '/dashboard/minha-conta'
    const isCheckoutRedirectPath = currentPath === '/checkout-redirect'

    // Case 1: Subscription/Trial is NOT active
    // This is the FIRST check to ensure users pay/start trial before filling profile
    // Allow access to the subscription page itself AND the checkout redirector
    if (!subscriptionActive && !isSubscriptionPath && !isCheckoutRedirectPath) {
        return <Navigate to="/checkout-redirect" replace />
    }

    // Case 2: User HAS accurate subscription but DOES NOT have a complete profile
    if (subscriptionActive && !profileComplete && !isCompleteProfilePath) {
        return <Navigate to="/complete-profile" replace />
    }

    // Case 3: User is on setup pages but everything is already complete
    if (subscriptionActive && profileComplete && (isCompleteProfilePath || currentPath === '/login')) {
        return <Navigate to="/dashboard" replace />
    }

    return <>{children}</>
}
